import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { technicianAPI } from './technicianAPI';

// Fetch all users
export const fetchUsers = createAsyncThunk(
  'technicians/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await technicianAPI.fetchUsers();
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Fetch dealer users
export const fetchDealerUsers = createAsyncThunk(
  'technicians/fetchDealerUsers',
  async (dealerId, { rejectWithValue }) => {
    try {
      const response = await technicianAPI.fetchDealerUsers(dealerId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Add new user
export const addUserAsync = createAsyncThunk(
  'technicians/addUser',
  async (userData, { dispatch, rejectWithValue }) => {
    try {
      const response = await technicianAPI.addUser(userData);
      console.log("Add user response:", response);
      dispatch(setSuccessMessage('Technician added successfully!'));
      return response;
    } catch (error) {
      console.error("Add user error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to add technician'));
      return rejectWithValue(error.message);
    }
  }
);

// Edit user
export const editUserAsync = createAsyncThunk(
  'technicians/editUser',
  async ({ userId, userData }, { dispatch, rejectWithValue }) => {
    try {
      const response = await technicianAPI.editUser(userId, userData);
      console.log("Edit user response:", response);
      dispatch(setSuccessMessage('Technician updated successfully!'));
      return response;
    } catch (error) {
      console.error("Edit user error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to update technician'));
      return rejectWithValue(error.message);
    }
  }
);

// Delete user
export const deleteUserAsync = createAsyncThunk(
  'technicians/deleteUser',
  async (userId, { dispatch, rejectWithValue }) => {
    try {
      const response = await technicianAPI.deleteUser(userId);
      console.log("Delete user response:", response);
      dispatch(setSuccessMessage('Technician deleted successfully!'));
      return response;
    } catch (error) {
      console.error("Delete user error:", error);
      dispatch(setErrorMessage(error.message || 'Failed to delete technician'));
      return rejectWithValue(error.message);
    }
  }
);

const technicianSlice = createSlice({
  name: 'technicians',
  initialState: {
    users: [],
    loading: false,
    error: null,
    successMessage: null,
    refreshData: false,
  },
  reducers: {
    setSuccessMessage: (state, action) => {
      state.successMessage = action.payload;
    },
    setErrorMessage: (state, action) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.successMessage = null;
      state.error = null;
    },
    setRefreshUsersData: (state, action) => {
      state.refreshData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all users
      .addCase(fetchUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch technicians';
      })
      // Fetch dealer users
      .addCase(fetchDealerUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDealerUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
      })
      .addCase(fetchDealerUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to fetch dealer technicians';
      })
      // Add user
      .addCase(addUserAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(addUserAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(addUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Edit user
      .addCase(editUserAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(editUserAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(editUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete user
      .addCase(deleteUserAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteUserAsync.fulfilled, (state) => {
        state.loading = false;
        state.refreshData = true;
      })
      .addCase(deleteUserAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSuccessMessage, 
  setErrorMessage, 
  clearMessages,
  setRefreshUsersData 
} = technicianSlice.actions;

export const selectUsers = (state) => state.technicians.users;
export const selectUsersLoading = (state) => state.technicians.loading;
export const selectUsersError = (state) => state.technicians.error;
export const selectSuccessMessage = (state) => state.technicians.successMessage;
export const selectRefreshUsersData = (state) => state.technicians.refreshData;

export default technicianSlice.reducer;